import { call, put, takeLatest } from 'redux-saga/effects';
import {
  CASE_FETCH_CASE_TAG_SUGGESTIONS_BEGIN,
  CASE_FETCH_CASE_TAG_SUGGESTIONS_SUCCESS,
  CASE_FETCH_CASE_TAG_SUGGESTIONS_FAILURE,
  CASE_FETCH_CASE_TAG_SUGGESTIONS_DISMISS_FEEDBACK,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

export const fetchCaseTagSuggestions = createPromiseAction(CASE_FETCH_CASE_TAG_SUGGESTIONS_BEGIN);

export function dismissFetchCaseTagSuggestionsFeedback() {
  return {
    type: CASE_FETCH_CASE_TAG_SUGGESTIONS_DISMISS_FEEDBACK,
  };
}

export function* doFetchCaseTagSuggestions(action) {
  const {
    payload: { caseId },
  } = action;
  const res = yield call(api.get, `/cases/${caseId}/tags/suggestions`);

  if (res && res.error) {
    yield put({
      type: CASE_FETCH_CASE_TAG_SUGGESTIONS_FAILURE,
      feedback: {
        message: 'feedback.fetchCaseTagSuggestionsFailure',
        error: res.error,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_FETCH_CASE_TAG_SUGGESTIONS_SUCCESS,
    data: res,
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchFetchCaseTagSuggestions() {
  yield takeLatest(fetchCaseTagSuggestions, withCurrentCaseId(doFetchCaseTagSuggestions));
}

export function useFetchCaseTagSuggestions() {
  const dispatch = useDispatch();

  const { caseTagSuggestions, caseTagSuggestionsPending, caseTagSuggestionsFeedback } = useSelector(
    (state) => ({
      caseTagSuggestions: state.case.caseTagSuggestions,
      caseTagSuggestionsPending: state.case.caseTagSuggestionsPending,
      caseTagSuggestionsFeedback: state.case.caseTagSuggestionsFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(fetchCaseTagSuggestions(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissFetchCaseTagSuggestionsFeedback());
  }, [dispatch]);

  return {
    caseTagSuggestions,
    fetchCaseTagSuggestions: boundAction,
    caseTagSuggestionsPending,
    caseTagSuggestionsFeedback,
    dismissFetchCaseTagSuggestionsFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_CASE_TAG_SUGGESTIONS_BEGIN + '.TRIGGER':
      return {
        ...state,
        caseTagSuggestionsPending: true,
        caseTagSuggestionsFeedback: null,
      };

    case CASE_FETCH_CASE_TAG_SUGGESTIONS_SUCCESS:
      return {
        ...state,
        caseTagSuggestionsPending: false,
        caseTagSuggestionsFeedback: action.feedback,
        caseTagSuggestions: action.data,
      };

    case CASE_FETCH_CASE_TAG_SUGGESTIONS_FAILURE:
      return {
        ...state,
        caseTagSuggestionsPending: false,
        caseTagSuggestionsFeedback: action.feedback,
      };

    case CASE_FETCH_CASE_TAG_SUGGESTIONS_DISMISS_FEEDBACK:
      return {
        ...state,
        caseTagSuggestionsFeedback: null,
      };

    default:
      return state;
  }
}
